import { useMemo } from "react";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { FinancialAccountSourceType } from "@common/types/financialAccount";
import {
    useEntitiesContexts,
    useEntitiesContextsQueryState,
} from "@hooks/useEntitiesContexts";
import { isBillProviderAccount } from "@helpers/bills";
import { BillsViewProvider } from "../../contexts/BillsViewProvider";
import { BillsConnectOverlay } from "./BillsConnectOverlay";
import { BillPageContent } from "./BillPageContent";

const BILL_SOURCE_TYPES = [
    FinancialAccountSourceType.RAMP,
    FinancialAccountSourceType.BILLCOM,
];

export const BillsPage: React.FC = () => {
    const entities = useEntitiesContexts();
    const entitiesQueryState = useEntitiesContextsQueryState();
    const { hasAccess, isFetched } = useAllowFeature(
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
    );

    const billAccounts = useMemo(() => {
        if (!entities) {
            return [];
        }

        return entities.flatMap((e) =>
            e.financialAccounts.filter((f) =>
                BILL_SOURCE_TYPES.includes(f.sourceType),
            ),
        );
    }, [entities]);

    if (entitiesQueryState?.status === "pending" || !isFetched) {
        return null;
    }

    // The importer (manual creation -> accrual ledger) is Plus-only, but viewing
    // synced bills is not. Only block users who lack the plan AND have no
    // connected bill provider to view; connecting reveals their bills.
    if (!hasAccess && !billAccounts.some(isBillProviderAccount)) {
        return <BillsConnectOverlay />;
    }

    return (
        <BillsViewProvider>
            <BillPageContent billAccounts={billAccounts} />
        </BillsViewProvider>
    );
};
